import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API, authFetch } from "@/App";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PageLayout from "@/components/PageLayout";
import { toast } from "sonner";
import { 
  Plus, PawPrint, Syringe, FileText, ChevronRight, 
  Calendar, AlertTriangle, Dog, Cat, Bird, Rabbit,
  MapPin, Users, Clock, Sparkles, Phone, Search,
  ShoppingBag, Lightbulb, Camera, Star, ArrowRight, FolderOpen,
  Check, X, Share2, Mail, AlertCircle
} from "lucide-react";
import BreedAutocomplete from "@/components/BreedAutocomplete";
import { getBreedsBySpecies } from "@/data/breeds";

// Horse icon component (custom since lucide doesn't have one)
const Horse = ({ size = 24, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M3.5 21h17M5 21V11l2-2 3-1 2 1v4h4l3-3v-1l2-1v4l-2 4v5"/>
    <path d="M14 7V4l3-1 2 2v2"/>
  </svg>
);

const speciesIcons = {
  dog: Dog,
  cat: Cat,
  bird: Bird,
  rabbit: Rabbit,
  horse: Horse,
  other: PawPrint
};

const speciesLabels = {
  dog: "Cane",
  cat: "Gatto",
  bird: "Uccello",
  rabbit: "Coniglio",
  horse: "Cavallo",
  other: "Altro"
};

// Helper to capitalize breed names properly
const formatBreed = (breed) => {
  if (!breed) return null;
  return breed
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Mini Calendar Component for Mobile with booking dots
function MiniCalendarMobile({ navigate, bookings = [] }) {
  const today = new Date();
  const dayNames = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
  const monthNames = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 
                      'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
  
  // Get next 7 days
  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    days.push(d);
  }

  // Check if a day has bookings
  const hasBookingOnDay = (date) => {
    return bookings.some(booking => {
      const bookingDate = new Date(booking.date || booking.booking_date);
      return bookingDate.toDateString() === date.toDateString();
    });
  };

  return (
    <div className="bg-white dark:bg-gradient-to-br dark:from-[#1A1A1A] dark:to-[#141414] rounded-2xl p-4 shadow-sm border border-black/5 dark:border-white/5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="text-[#FF6B4A]" size={18} />
          <h3 className="font-bold text-[#1D1D1F] dark:text-white">{monthNames[today.getMonth()]}</h3>
        </div>
        <button 
          onClick={() => navigate('/bookings')}
          className="text-[#FF6B4A] text-sm font-medium"
        >
          Vedi tutto
        </button>
      </div>
      
      <div className="flex justify-between">
        {days.map((day, i) => {
          const isToday = i === 0;
          const hasBooking = hasBookingOnDay(day);
          return (
            <div 
              key={i}
              className={`flex flex-col items-center p-2 rounded-xl transition-all relative ${
                isToday ? 'bg-[#FF6B4A] text-white' : 'text-[#1D1D1F] dark:text-white'
              }`}
            >
              <span className={`text-xs ${isToday ? 'text-white/80' : 'text-[#86868B] dark:text-gray-400'}`}>
                {dayNames[day.getDay()]}
              </span>
              <span className="text-lg font-bold">{day.getDate()}</span>
              {/* Booking dot indicator */}
              {hasBooking && (
                <div className={`absolute -bottom-0.5 w-1.5 h-1.5 rounded-full ${
                  isToday ? 'bg-white' : 'bg-[#FF6B4A]'
                }`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Mini Calendar Component for Desktop - Full month view with booking dots
function MiniCalendarDesktop({ navigate, bookings = [] }) {
  const today = new Date();
  const monthNames = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 
                      'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
  const dayNames = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
  
  // Get days in current month
  const year = today.getFullYear();
  const month = today.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  
  // Build calendar grid
  const calendarDays = [];
  for (let i = 0; i < firstDay; i++) calendarDays.push(null);
  for (let i = 1; i <= daysInMonth; i++) calendarDays.push(i);

  // Check if a day has bookings
  const hasBookingOnDay = (day) => {
    if (!day) return false;
    const checkDate = new Date(year, month, day);
    return bookings.some(booking => {
      const bookingDate = new Date(booking.date || booking.booking_date);
      return bookingDate.toDateString() === checkDate.toDateString();
    });
  };

  return (
    <div className="bg-white rounded-3xl p-6 shadow-sm border border-black/5 h-full">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="text-[#FF6B4A]" size={20} />
          <h2 className="text-xl font-bold text-[#1D1D1F]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {monthNames[month]} {year}
          </h2>
        </div>
        <button 
          onClick={() => navigate('/bookings')}
          className="text-[#FF6B4A] font-semibold text-sm hover:underline"
        >
          Vedi tutte le prenotazioni
        </button>
      </div>
      
      {/* Day headers */}
      <div className="grid grid-cols-7 gap-2 mb-2">
        {dayNames.map(day => (
          <div key={day} className="text-center text-xs font-medium text-[#86868B] py-1">
            {day}
          </div>
        ))}
      </div>
      
      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-2">
        {calendarDays.map((day, idx) => {
          if (!day) return <div key={idx} className="aspect-square" />;
          
          const isToday = day === today.getDate();
          const hasBooking = hasBookingOnDay(day);
          
          return (
            <button
              key={idx}
              onClick={() => navigate('/bookings')}
              className={`aspect-square rounded-lg flex flex-col items-center justify-center relative transition-all hover:scale-105 ${
                isToday ? 'bg-[#1D1D1F] text-white' :
                hasBooking ? 'bg-[#FF6B4A]/10' : 'hover:bg-[#F5F5F7]'
              }`}
            >
              <span className="font-medium text-sm">{day}</span>
              {hasBooking && !isToday && (
                <div className="absolute bottom-1 w-1.5 h-1.5 rounded-full bg-[#FF6B4A]" />
              )}
              {hasBooking && isToday && (
                <div className="absolute bottom-1 w-1.5 h-1.5 rounded-full bg-white" />
              )}
            </button>
          );
        })}
      </div>
      
      {/* Upcoming bookings preview */}
      {bookings.length > 0 && (
        <div className="mt-4 pt-4 border-t border-black/5">
          <p className="text-sm text-[#86868B] mb-2">Prossimi appuntamenti</p>
          <div className="space-y-2">
            {bookings.slice(0, 2).map((booking, i) => (
              <div key={i} className="flex items-center gap-3 p-2 rounded-xl bg-[#F5F5F7]">
                <div className="w-2 h-2 rounded-full bg-[#FF6B4A]" />
                <div className="flex-1">
                  <p className="font-medium text-sm text-[#1D1D1F]">{booking.professional_name}</p>
                  <p className="text-xs text-[#86868B]">{booking.date} • {booking.time_slot}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pets, setPets] = useState([]);
  const [sharedPets, setSharedPets] = useState([]);
  const [pendingShares, setPendingShares] = useState([]);
  const [stats, setStats] = useState(null);
  const [tips, setTips] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mounted, setMounted] = useState(false);
  const [showAddPet, setShowAddPet] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [emailVerification, setEmailVerification] = useState({ verified: true, expired: false, resending: false });
  
  const [newPet, setNewPet] = useState({
    name: "",
    species: "dog",
    breed: "",
    birth_date: "",
    weight: "",
    microchip: "",
    photo_url: ""
  });

  useEffect(() => {
    setMounted(true);
    setIsMobile(window.innerWidth < 1024);
    
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    fetchData();
    
    // Refresh data when page becomes visible (e.g., returning from Pets page)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchData();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also refresh on focus (for mobile apps)
    const handleFocus = () => fetchData();
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const fetchData = async () => {
    try {
      const [petsRes, statsRes, tipsRes, productsRes, bookingsRes, sharedRes, pendingRes, verifyRes] = await Promise.all([
        authFetch(`${API}/pets`),
        authFetch(`${API}/dashboard/stats`),
        authFetch(`${API}/tips/for-me`),
        fetch(`${API}/shop/featured`),
        authFetch(`${API}/bookings`),
        authFetch(`${API}/shared-pets`),
        authFetch(`${API}/pending-shares`),
        authFetch(`${API}/auth/verification-status`)
      ]);
      
      // Only update pets if we got a valid response (don't clear existing data on error)
      if (petsRes.ok) {
        const petsData = await petsRes.json();
        if (Array.isArray(petsData)) {
          setPets(petsData);
        }
      }
      if (statsRes.ok) setStats(await statsRes.json());
      if (tipsRes.ok) {
        const tipsData = await tipsRes.json();
        setTips(tipsData.tips?.slice(0, 2) || []);
      }
      if (productsRes.ok) setFeaturedProducts(await productsRes.json());
      if (bookingsRes.ok) setBookings(await bookingsRes.json());
      if (sharedRes.ok) {
        const sharedData = await sharedRes.json();
        setSharedPets(sharedData.shared_pets || []);
      }
      if (pendingRes.ok) {
        const pendingData = await pendingRes.json();
        setPendingShares(pendingData.pending_shares || []);
      }
      if (verifyRes.ok) {
        const verifyData = await verifyRes.json();
        setEmailVerification({
          verified: verifyData.email_verified,
          expired: verifyData.verification_expired,
          resending: false,
          authProvider: verifyData.auth_provider
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const acceptShare = async (shareId) => {
    try {
      const res = await authFetch(`${API}/shares/${shareId}/accept`, { method: "POST" });
      if (res.ok) {
        toast.success("Invito accettato!");
        fetchData();
      }
    } catch (e) {
      toast.error("Errore");
    }
  };
  
  const rejectShare = async (shareId) => {
    try {
      const res = await authFetch(`${API}/shares/${shareId}/reject`, { method: "POST" });
      if (res.ok) {
        toast.success("Invito rifiutato");
        fetchData();
      }
    } catch (e) {
      toast.error("Errore");
    }
  };

  const resendVerificationEmail = async () => {
    if (!user?.email) return;
    setEmailVerification(prev => ({ ...prev, resending: true }));
    
    try {
      const res = await fetch(`${API}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email })
      });
      
      if (res.ok) {
        toast.success('Email di verifica inviata! Controlla la tua casella.');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Errore nell\'invio');
      }
    } catch (e) {
      toast.error('Errore di connessione');
    } finally {
      setEmailVerification(prev => ({ ...prev, resending: false }));
    }
  };

  const handleAddPet = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const petData = {
        ...newPet,
        weight: newPet.weight ? parseFloat(newPet.weight) : null
      };
      
      const response = await authFetch(`${API}/pets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(petData)
      });
      
      if (response.ok) {
        const pet = await response.json();
        setPets([...pets, pet]);
        setShowAddPet(false);
        setNewPet({ name: "", species: "dog", breed: "", birth_date: "", weight: "", microchip: "", photo_url: "" });
        toast.success("Animale aggiunto!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nell'aggiunta");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Buongiorno";
    if (hour < 18) return "Buon pomeriggio";
    return "Buonasera";
  };

  const getDefaultPetImage = (species) => {
    const images = {
      dog: "https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=400&q=80",
      cat: "https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=400&q=80",
      bird: "https://images.unsplash.com/photo-1452570053594-1b985d6ea890?w=400&q=80",
      rabbit: "https://images.unsplash.com/photo-1585110396000-c9ffd4e4b308?w=400&q=80",
      horse: "https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?w=400&q=80",
      other: "https://images.unsplash.com/photo-1425082661705-1834bfd09dca?w=400&q=80"
    };
    return images[species] || images.dog;
  };

  // Pet image with fallback
  const getPetImage = (pet) => {
    if (pet.photo_url) {
      // Handle both absolute URLs and relative paths
      if (pet.photo_url.startsWith('http')) {
        return pet.photo_url;
      }
      // Handle local uploads (e.g., /api/uploads/...)
      if (pet.photo_url.startsWith('/')) {
        return `${API.replace('/api', '')}${pet.photo_url}`;
      }
      return pet.photo_url;
    }
    return getDefaultPetImage(pet.species);
  };

  const firstName = user?.name?.split(' ')[0] || 'Amico';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="relative">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#FF6B4A] to-[#FFB088] animate-pulse" />
          <PawPrint className="absolute inset-0 m-auto text-white" size={32} />
        </div>
      </div>
    );
  }

  // Mobile Layout
  if (isMobile) {
    return (
      <MobileDashboard 
        user={user}
        pets={pets}
        stats={stats}
        tips={tips}
        featuredProducts={featuredProducts}
        bookings={bookings}
        mounted={mounted}
        getGreeting={getGreeting}
        getPetImage={getPetImage}
        firstName={firstName}
        navigate={navigate}
        showAddPet={showAddPet}
        setShowAddPet={setShowAddPet}
        newPet={newPet}
        setNewPet={setNewPet}
        handleAddPet={handleAddPet}
        isSubmitting={isSubmitting}
      />
    );
  }

  // Desktop Layout - Clean Apple-style
  return (
    <PageLayout data-testid="dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <header className={`mb-8 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
          <p className="text-[#86868B] text-sm font-medium mb-1">{getGreeting()}</p>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#1D1D1F] dark:text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ciao, {firstName}
          </h1>
        </header>

        {/* Email Verification Banner */}
        {!emailVerification.verified && emailVerification.authProvider === 'email' && (
          <div className={`mb-6 p-4 rounded-2xl border ${
            emailVerification.expired 
              ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800' 
              : 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800'
          }`}>
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-full ${
                emailVerification.expired ? 'bg-red-100 dark:bg-red-900/30' : 'bg-amber-100 dark:bg-amber-900/30'
              }`}>
                {emailVerification.expired ? (
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                ) : (
                  <Mail className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                )}
              </div>
              <div className="flex-1">
                <h3 className={`font-semibold ${
                  emailVerification.expired ? 'text-red-800 dark:text-red-200' : 'text-amber-800 dark:text-amber-200'
                }`}>
                  {emailVerification.expired ? 'Verifica email scaduta' : 'Verifica la tua email'}
                </h3>
                <p className={`text-sm mt-1 ${
                  emailVerification.expired ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300'
                }`}>
                  {emailVerification.expired 
                    ? 'Il tempo per verificare la tua email è scaduto. Richiedi un nuovo link per continuare ad usare Zampa.'
                    : 'Controlla la tua casella email e clicca sul link di verifica per attivare il tuo account.'}
                </p>
              </div>
              <Button
                onClick={resendVerificationEmail}
                disabled={emailVerification.resending}
                size="sm"
                className={`${
                  emailVerification.expired 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-amber-600 hover:bg-amber-700'
                } text-white rounded-xl`}
              >
                {emailVerification.resending ? 'Invio...' : 'Reinvia email'}
              </Button>
            </div>
          </div>
        )}

        {/* NEW DASHBOARD LAYOUT */}
        <div className="grid grid-cols-12 gap-6">
          
          {/* Main Content - 8 columns */}
          <div className="col-span-8 space-y-6">
            
            {/* Summary Stats Row */}
            <div className={`grid grid-cols-4 gap-4 transition-all duration-700 delay-100 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <div className="bg-gradient-to-br from-[#FF6B4A] to-[#FF8F6B] rounded-2xl p-4 text-white">
                <PawPrint size={24} className="mb-2 opacity-80" />
                <p className="text-2xl font-bold">{pets.length}</p>
                <p className="text-sm text-white/70">Animali</p>
              </div>
              <div 
                onClick={() => pets.length > 0 && navigate(`/fascicolo/${pets[0].pet_id}?tab=vaccines`)}
                className="bg-white rounded-2xl p-4 border border-black/5 hover:shadow-md transition-all cursor-pointer"
              >
                <Syringe size={24} className="mb-2 text-emerald-500" />
                <p className="text-2xl font-bold text-[#1D1D1F]">{stats?.vaccines_count || 0}</p>
                <p className="text-sm text-[#86868B]">Vaccini</p>
              </div>
              <div 
                onClick={() => navigate('/documents')}
                className="bg-white rounded-2xl p-4 border border-black/5 hover:shadow-md transition-all cursor-pointer"
              >
                <FileText size={24} className="mb-2 text-blue-500" />
                <p className="text-2xl font-bold text-[#1D1D1F]">{stats?.documents_count || 0}</p>
                <p className="text-sm text-[#86868B]">Documenti</p>
              </div>
              <div 
                onClick={() => navigate('/bookings')}
                className="bg-white rounded-2xl p-4 border border-black/5 hover:shadow-md transition-all cursor-pointer"
              >
                <Calendar size={24} className="mb-2 text-purple-500" />
                <p className="text-2xl font-bold text-[#1D1D1F]">{stats?.bookings_count || 0}</p>
                <p className="text-sm text-[#86868B]">Prenotazioni</p>
              </div>
            </div>
            
            {/* Pets Section */}
            <div className={`transition-all duration-700 delay-150 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-[#1D1D1F]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  I tuoi animali
                </h2>
                <button 
                  onClick={() => setShowAddPet(true)}
                  className="flex items-center gap-2 text-[#FF6B4A] font-semibold hover:underline text-sm"
                  data-testid="add-pet-btn"
                >
                  <Plus size={16} />
                  Aggiungi
                </button>
              </div>

              {pets.length === 0 ? (
                <div className="bg-white rounded-2xl p-8 shadow-sm border border-black/5 text-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#FF6B4A]/20 to-[#FF6B4A]/10 flex items-center justify-center mx-auto mb-4">
                    <PawPrint className="text-[#FF6B4A]" size={32} />
                  </div>
                  <h3 className="font-bold text-[#1D1D1F] text-lg mb-2">Nessun animale registrato</h3>
                  <p className="text-[#86868B] text-sm mb-4">Aggiungi il tuo primo amico peloso!</p>
                  <Button onClick={() => setShowAddPet(true)} className="bg-[#1D1D1F] rounded-xl px-6">
                    <Plus size={16} className="mr-2" />
                    Aggiungi animale
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {pets.map((pet) => {
                    const SpeciesIcon = speciesIcons[pet.species] || PawPrint;
                    const age = pet.birth_date ? Math.floor((new Date() - new Date(pet.birth_date)) / (365.25 * 24 * 60 * 60 * 1000)) : null;
                    
                    return (
                      <div
                        key={pet.pet_id}
                        onClick={() => navigate(`/fascicolo/${pet.pet_id}`)}
                        className="bg-white rounded-2xl p-4 shadow-sm border border-black/5 cursor-pointer hover:shadow-md hover:scale-[1.005] transition-all"
                        data-testid={`pet-card-${pet.pet_id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 bg-[#F5F5F7]">
                            <img 
                              src={getPetImage(pet)} 
                              alt={pet.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-bold text-[#1D1D1F]">{pet.name}</h3>
                              {pet.microchip && (
                                <span className="text-[10px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-md">Microchip</span>
                              )}
                            </div>
                            <p className="text-[#86868B] text-sm">{formatBreed(pet.breed) || speciesLabels[pet.species]}</p>
                          </div>
                          {/* Mini stats for this pet */}
                          <div className="flex items-center gap-3 text-xs text-[#86868B]">
                            <div className="text-center">
                              <Syringe size={14} className="mx-auto mb-0.5 text-emerald-500" />
                              <span>{pet.vaccines_count || 0}</span>
                            </div>
                            <div className="text-center">
                              <FileText size={14} className="mx-auto mb-0.5 text-blue-500" />
                              <span>{pet.documents_count || 0}</span>
                            </div>
                          </div>
                          <ChevronRight className="text-[#D1D1D6]" size={18} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Alerts & Activity Row */}
            <div className={`grid grid-cols-2 gap-4 transition-all duration-700 delay-200 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              
              {/* Upcoming Vaccines */}
              <div className="bg-white rounded-2xl p-4 border border-black/5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                    <AlertTriangle size={16} className="text-amber-600" />
                  </div>
                  <h3 className="font-semibold text-[#1D1D1F] text-sm">Prossimi Vaccini</h3>
                </div>
                {stats?.upcoming_vaccines?.length > 0 ? (
                  <div className="space-y-2">
                    {stats.upcoming_vaccines.slice(0, 3).map((v, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <span className="text-[#1D1D1F]">{v.pet_name} - {v.vaccine_name}</span>
                        <span className="text-amber-600">{new Date(v.expiry_date).toLocaleDateString('it-IT')}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-[#86868B]">Nessun vaccino in scadenza</p>
                )}
              </div>

              {/* Recent Activity */}
              <div className="bg-white rounded-2xl p-4 border border-black/5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Clock size={16} className="text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-[#1D1D1F] text-sm">Attività Recente</h3>
                </div>
                {bookings?.length > 0 ? (
                  <div className="space-y-2">
                    {bookings.slice(0, 3).map((b, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <span className="text-[#1D1D1F]">{b.service_name || 'Prenotazione'}</span>
                        <span className="text-[#86868B]">{new Date(b.date || b.booking_date).toLocaleDateString('it-IT')}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-[#86868B]">Nessuna attività recente</p>
                )}
              </div>
            </div>

            {/* Shared Pets */}
            {sharedPets.length > 0 && (
              <div className={`transition-all duration-700 delay-250 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                <h3 className="text-sm font-semibold text-[#86868B] uppercase tracking-wider mb-3">Condivisi con te</h3>
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {sharedPets.map((pet) => (
                    <div
                      key={pet.pet_id}
                      onClick={() => navigate(`/shared/${pet.share_id}`)}
                      className="flex-shrink-0 bg-white rounded-xl p-3 border border-black/5 cursor-pointer hover:shadow-sm transition-all"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-lg overflow-hidden bg-[#F5F5F7]">
                          <img src={getPetImage(pet)} alt={pet.name} className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <p className="font-semibold text-[#1D1D1F] text-sm">{pet.name}</p>
                          <p className="text-xs text-[#86868B]">{pet.shared_by_name}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Sidebar - 4 columns */}
          <div className="col-span-4 space-y-4">
            
            {/* Mini Calendar */}
            <div className={`transition-all duration-700 delay-150 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <MiniCalendarDesktop navigate={navigate} bookings={bookings} />
            </div>

            {/* Quick Actions */}
            <div className={`bg-white rounded-2xl p-4 border border-black/5 transition-all duration-700 delay-200 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <h3 className="text-sm font-semibold text-[#86868B] uppercase tracking-wider mb-3">Accesso rapido</h3>
              <div className="space-y-2">
                {[
                  { path: '/marketplace', icon: MapPin, label: 'Mappa Servizi', color: 'text-blue-600', bg: 'bg-blue-50' },
                  { path: '/services', icon: Users, label: 'Servizi a Domicilio', color: 'text-purple-600', bg: 'bg-purple-50' },
                  { path: '/documents', icon: FolderOpen, label: 'Cloud Documenti', color: 'text-teal-600', bg: 'bg-teal-50' },
                  { path: '/tips', icon: Lightbulb, label: 'Consigli', color: 'text-amber-600', bg: 'bg-amber-50' },
                  { path: '/shop', icon: ShoppingBag, label: 'Shop', color: 'text-pink-600', bg: 'bg-pink-50' },
                ].map((item) => (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className="w-full rounded-xl p-2.5 text-left flex items-center gap-3 hover:bg-[#F5F5F7] transition-all"
                  >
                    <div className={`w-8 h-8 rounded-lg ${item.bg} flex items-center justify-center`}>
                      <item.icon className={item.color} size={16} />
                    </div>
                    <span className="font-medium text-[#1D1D1F] text-sm">{item.label}</span>
                    <ChevronRight className="text-[#C7C7CC] ml-auto" size={14} />
                  </button>
                ))}
              </div>
            </div>

            {/* Emergency Buttons */}
            <div className={`grid grid-cols-2 gap-2 transition-all duration-700 delay-250 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <button
                onClick={() => navigate('/emergency')}
                className="bg-red-500 rounded-xl p-3 text-left text-white hover:bg-red-600 transition-all"
              >
                <Phone size={16} className="mb-1" />
                <p className="font-semibold text-xs">Emergenza</p>
              </button>
              <button
                onClick={() => navigate('/lost-pets')}
                className="bg-amber-100 rounded-xl p-3 text-left hover:bg-amber-200 transition-all"
              >
                <Search size={16} className="mb-1 text-amber-700" />
                <p className="font-semibold text-xs text-amber-800">Smarriti</p>
              </button>
            </div>

            {/* Pending Share Invites */}
            {pendingShares.length > 0 && (
              <div className={`bg-blue-50 rounded-2xl p-4 border border-blue-200 transition-all duration-700 delay-300 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Share2 size={16} className="text-blue-600" />
                  <h3 className="font-semibold text-blue-800 text-sm">Inviti in sospeso</h3>
                </div>
                {pendingShares.slice(0, 2).map((share) => (
                  <div key={share.share_id} className="bg-white rounded-lg p-2 mb-2">
                    <p className="text-xs text-[#1D1D1F] font-medium">{share.pet_name}</p>
                    <p className="text-[10px] text-[#86868B]">Da: {share.shared_by_name}</p>
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => acceptShare(share.share_id)} className="flex-1 bg-emerald-500 text-white text-xs py-1 rounded-md hover:bg-emerald-600">
                        <Check size={12} className="inline mr-1" />Accetta
                      </button>
                      <button onClick={() => rejectShare(share.share_id)} className="flex-1 bg-red-100 text-red-600 text-xs py-1 rounded-md hover:bg-red-200">
                        <X size={12} className="inline mr-1" />Rifiuta
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Pet Dialog */}
      <AddPetDialog 
        open={showAddPet}
        onOpenChange={setShowAddPet}
        newPet={newPet}
        setNewPet={setNewPet}
        handleAddPet={handleAddPet}
        isSubmitting={isSubmitting}
      />
    </PageLayout>
  );
}

// Mobile Dashboard Component (original layout)
function MobileDashboard({ user, pets, stats, tips, featuredProducts, bookings = [], mounted, getGreeting, getPetImage, firstName, navigate, showAddPet, setShowAddPet, newPet, setNewPet, handleAddPet, isSubmitting }) {
  
  // Calculate pet age
  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const birth = new Date(birthDate);
    const now = new Date();
    const years = now.getFullYear() - birth.getFullYear();
    const months = now.getMonth() - birth.getMonth();
    if (years > 0) return `${years} ${years === 1 ? 'anno' : 'anni'}`;
    if (months > 0) return `${months} ${months === 1 ? 'mese' : 'mesi'}`;
    return 'Cucciolo';
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-28" data-testid="dashboard-mobile">
      <div className="max-w-lg mx-auto">
        {/* Header - Clean */}
        <header className={`px-5 pt-8 pb-4 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
          <p className="text-[#86868B] text-sm font-medium">{getGreeting()}</p>
          <h1 className="text-2xl font-bold text-[#1D1D1F] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ciao, {firstName}
          </h1>
        </header>

        {/* Quick Actions Hero - More Functional */}
        <section className={`px-5 pb-4 transition-all duration-700 delay-100 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="bg-gradient-to-br from-[#FF6B4A] via-[#FF8F6B] to-[#FFB088] rounded-2xl p-4 overflow-hidden">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <PawPrint className="text-white" size={24} />
                </div>
                <div>
                  <p className="text-white font-bold text-lg">{pets.length} {pets.length === 1 ? 'Animale' : 'Animali'}</p>
                  <p className="text-white/70 text-sm">{stats?.vaccines_count || 0} vaccini • {stats?.documents_count || 0} documenti</p>
                </div>
              </div>
              <button
                onClick={() => setShowAddPet(true)}
                className="w-10 h-10 rounded-xl bg-white flex items-center justify-center"
                data-testid="add-pet-btn-mobile"
              >
                <Plus className="text-[#FF6B4A]" size={20} />
              </button>
            </div>
          </div>
        </section>

        {/* Quick Stats Row - Smaller */}
        {stats && (
          <section className={`px-5 pb-4 transition-all duration-700 delay-150 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="flex gap-2">
              <button 
                onClick={() => pets.length > 0 && navigate(`/fascicolo/${pets[0].pet_id}?tab=vaccines`)}
                className="flex-1 bg-white rounded-xl p-3 border border-black/5 active:scale-95 transition-transform"
              >
                <Syringe className="text-emerald-500 mb-1" size={20} />
                <p className="text-lg font-bold text-[#1D1D1F]">{stats.vaccines_count}</p>
                <p className="text-xs text-[#86868B]">Vaccini</p>
              </button>
              <button 
                onClick={() => pets.length > 0 && navigate(`/fascicolo/${pets[0].pet_id}?tab=documents`)}
                className="flex-1 bg-white rounded-xl p-3 border border-black/5 active:scale-95 transition-transform"
              >
                <FileText className="text-blue-500 mb-1" size={20} />
                <p className="text-lg font-bold text-[#1D1D1F]">{stats.documents_count}</p>
                <p className="text-xs text-[#86868B]">Documenti</p>
              </button>
              <button 
                onClick={() => navigate('/bookings')}
                className="flex-1 bg-white rounded-xl p-3 border border-black/5 active:scale-95 transition-transform"
              >
                <Calendar className="text-purple-500 mb-1" size={20} />
                <p className="text-lg font-bold text-[#1D1D1F]">{stats.bookings_count || 0}</p>
                <p className="text-xs text-[#86868B]">Appuntamenti</p>
              </button>
            </div>
          </section>
        )}

        {/* My Pets Section - Single Column with More Info */}
        <section className={`px-5 pb-4 transition-all duration-700 delay-200 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-[#1D1D1F]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              I miei animali
            </h2>
            <button className="text-[#FF6B4A] text-sm font-semibold" onClick={() => setShowAddPet(true)}>
              + Aggiungi
            </button>
          </div>

          {pets.length === 0 ? (
            <div className="bg-white rounded-2xl p-6 text-center border border-black/5">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#FF6B4A]/20 to-[#FF6B4A]/10 flex items-center justify-center mx-auto mb-3">
                <PawPrint className="text-[#FF6B4A]" size={28} />
              </div>
              <h3 className="font-bold text-[#1D1D1F] mb-1">Nessun animale</h3>
              <p className="text-[#86868B] text-sm mb-4">Aggiungi il tuo primo amico!</p>
              <Button onClick={() => setShowAddPet(true)} className="bg-[#1D1D1F] rounded-xl">
                <Plus size={16} className="mr-2" />
                Aggiungi
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {pets.map((pet) => {
                const SpeciesIcon = speciesIcons[pet.species] || PawPrint;
                const age = calculateAge(pet.birth_date);
                return (
                  <div
                    key={pet.pet_id}
                    onClick={() => navigate(`/fascicolo/${pet.pet_id}`)}
                    className="bg-white rounded-2xl p-4 border border-black/5 cursor-pointer hover:shadow-md transition-all active:scale-[0.98]"
                    data-testid={`pet-card-${pet.pet_id}`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Photo */}
                      <div className="w-20 h-20 rounded-2xl overflow-hidden flex-shrink-0 bg-[#F5F5F7]">
                        <img 
                          src={getPetImage(pet)} 
                          alt={pet.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-bold text-[#1D1D1F] text-lg">{pet.name}</h3>
                            <p className="text-[#86868B] text-sm">{formatBreed(pet.breed) || speciesLabels[pet.species]}</p>
                          </div>
                          <ChevronRight className="text-[#D1D1D6] mt-1" size={18} />
                        </div>
                        
                        {/* Details Row */}
                        <div className="flex items-center gap-3 mt-2 text-xs">
                          {age && (
                            <span className="flex items-center gap-1 text-[#6E6E73]">
                              <Calendar size={12} />
                              {age}
                            </span>
                          )}
                          {pet.sex && (
                            <span className="flex items-center gap-1 text-[#6E6E73]">
                              {pet.sex === 'Maschio' ? '♂' : '♀'} {pet.sex}
                            </span>
                          )}
                          {pet.microchip && (
                            <span className="flex items-center gap-1 text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">
                              Microchip ✓
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Mini Calendar - Mobile */}
        <section className={`px-5 pb-4 transition-all duration-700 delay-250 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <MiniCalendarMobile navigate={navigate} bookings={bookings} />
        </section>

        {/* Quick Actions Grid */}
        <section className={`px-5 pb-4 transition-all duration-700 delay-300 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <h2 className="text-lg font-bold text-[#1D1D1F] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Esplora
          </h2>
          <div className="grid grid-cols-2 gap-2">
            {[
              { path: '/bookings', icon: Calendar, label: 'Prenotazioni', color: 'bg-purple-50 text-purple-600' },
              { path: '/marketplace', icon: MapPin, label: 'Mappa Servizi', color: 'bg-blue-50 text-blue-600' },
              { path: '/vets', icon: Search, label: 'Veterinari', color: 'bg-emerald-50 text-emerald-600' },
              { path: '/shop', icon: ShoppingBag, label: 'Shop', color: 'bg-pink-50 text-pink-600' },
            ].map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className="bg-white border border-black/5 rounded-xl p-4 text-left transition-all hover:shadow-sm active:scale-[0.98]"
              >
                <div className={`w-10 h-10 rounded-xl ${item.color} flex items-center justify-center mb-2`}>
                  <item.icon size={20} />
                </div>
                <p className="font-semibold text-[#1D1D1F] text-sm">{item.label}</p>
              </button>
            ))}
          </div>
        </section>

        {/* Emergency & Lost - Compact */}
        <section className={`px-5 pb-6 transition-all duration-700 delay-350 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => navigate('/emergency')}
              className="bg-[#1D1D1F] rounded-xl p-3 text-left text-white"
            >
              <Phone className="mb-1" size={18} />
              <p className="font-semibold text-sm">Emergenza</p>
            </button>
            <button
              onClick={() => navigate('/lost-pets')}
              className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-left"
            >
              <Search className="mb-1 text-amber-600" size={18} />
              <p className="font-semibold text-[#1D1D1F] text-sm">Smarriti</p>
            </button>
          </div>
        </section>
      </div>

      <AddPetDialog 
        open={showAddPet}
        onOpenChange={setShowAddPet}
        newPet={newPet}
        setNewPet={setNewPet}
        handleAddPet={handleAddPet}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}

// Add Pet Dialog Component
function AddPetDialog({ open, onOpenChange, newPet, setNewPet, handleAddPet, isSubmitting }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[420px] rounded-3xl bg-white p-0 border-0 shadow-2xl max-h-[90vh] overflow-hidden">
        <div className="p-6 overflow-y-auto max-h-[85vh]" style={{ WebkitOverflowScrolling: 'touch' }}>
          <DialogHeader className="mb-5">
            <DialogTitle className="text-xl font-bold text-[#1D1D1F] flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF6B4A] to-[#FF8F6B] flex items-center justify-center">
                <Sparkles className="text-white" size={20} />
              </div>
              Nuovo animale
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleAddPet} className="space-y-4">
            <div>
              <Label className="text-[#1D1D1F] text-sm font-semibold mb-2 block">Nome *</Label>
              <Input
                value={newPet.name}
                onChange={(e) => setNewPet({...newPet, name: e.target.value})}
                placeholder="Come si chiama?"
                className="h-12 rounded-xl bg-[#F5F5F7] border-0 font-medium text-base"
                required
                data-testid="pet-name-input"
              />
            </div>

            <div>
              <Label className="text-[#1D1D1F] text-sm font-semibold mb-2 block">Tipo *</Label>
              <Select value={newPet.species} onValueChange={(value) => setNewPet({...newPet, species: value, breed: ""})}>
                <SelectTrigger className="h-12 rounded-xl bg-[#F5F5F7] border-0 font-medium text-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value="dog">Cane</SelectItem>
                  <SelectItem value="cat">Gatto</SelectItem>
                  <SelectItem value="horse">Cavallo</SelectItem>
                  <SelectItem value="bird">Uccello</SelectItem>
                  <SelectItem value="rabbit">Coniglio</SelectItem>
                  <SelectItem value="other">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[#1D1D1F] text-sm font-semibold mb-2 block">Razza</Label>
              <BreedAutocomplete
                value={newPet.breed}
                onChange={(val) => setNewPet({...newPet, breed: val})}
                breeds={getBreedsBySpecies(newPet.species)}
                placeholder="Cerca o seleziona..."
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#1D1D1F] text-sm font-semibold mb-2 block">Data di nascita</Label>
                <Input
                  type="date"
                  value={newPet.birth_date}
                  onChange={(e) => setNewPet({...newPet, birth_date: e.target.value})}
                  className="h-12 rounded-xl bg-[#F5F5F7] border-0 text-base"
                />
              </div>
              <div>
                <Label className="text-[#1D1D1F] text-sm font-semibold mb-2 block">Peso (kg)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={newPet.weight}
                  onChange={(e) => setNewPet({...newPet, weight: e.target.value})}
                  placeholder="0.0"
                  className="h-12 rounded-xl bg-[#F5F5F7] border-0 text-base"
                />
              </div>
            </div>

            <div>
              <Label className="text-[#1D1D1F] text-sm font-semibold mb-2 block">Microchip</Label>
              <Input
                value={newPet.microchip}
                onChange={(e) => setNewPet({...newPet, microchip: e.target.value})}
                placeholder="Numero microchip"
                className="h-12 rounded-xl bg-[#F5F5F7] border-0 font-mono text-base"
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-12 bg-[#1D1D1F] hover:bg-black text-white rounded-xl font-bold mt-2"
            >
              {isSubmitting ? "Aggiunta in corso..." : "Aggiungi animale"}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
